import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, authHeaders, fmt, StatusBadge, TH, TD, AdminCard } from "./AdminUtils";

const STAT_DEFS = [
  {
    key: "totalRevenue",
    label: "Revenue",
    sub: "from paid orders",
    color: "var(--accent)",
    glow: "rgba(16,185,129,0.18)",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
  },
  {
    key: "totalOrders",
    label: "Orders",
    subFn: (s) => `${s.paidOrders} paid · ${s.pendingOrders} pending`,
    color: "#60a5fa",
    glow: "rgba(96,165,250,0.18)",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 01-8 0"/>
      </svg>
    ),
  },
  {
    key: "totalUsers",
    label: "Users",
    sub: "registered accounts",
    color: "#34d399",
    glow: "rgba(52,211,153,0.18)",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    key: "totalProducts",
    label: "Products",
    sub: "in catalogue",
    color: "#c084fc",
    glow: "rgba(192,132,252,0.18)",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
  },
];

export default function AdminOverview() {
  const [stats, setStats]               = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/dashboard`, { headers: authHeaders() }),
      axios.get(`${API}/admin/orders`,    { headers: authHeaders() }),
    ]).then(([statsRes, ordersRes]) => {
      setStats(statsRes.data);
      const sorted = [...(ordersRes.data.content || [])].sort(
        (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
      );
      setRecentOrders(sorted.slice(0, 8));
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="tc-loading">
        <div className="tc-spinner" />
        <p style={{ fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 12 }}>
          Loading dashboard…
        </p>
      </div>
    );
  }

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  // Revenue bar percentage
  const paidRatio = stats
    ? stats.totalOrders > 0 ? (stats.paidOrders / stats.totalOrders) * 100 : 0
    : 0;

  return (
    <div style={{ animation: "ovFadeIn 0.35s ease both" }}>
      <style>{`
        @keyframes ovFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ovCardIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ovBarGrow {
          from { width: 0; }
          to   { width: var(--bar-width); }
        }
        .ov-stat-card {
          transition: transform 0.18s ease, box-shadow 0.18s ease;
          cursor: default;
        }
        .ov-stat-card:hover {
          transform: translateY(-2px);
        }
        .ov-view-all:hover {
          color: var(--accent-hover) !important;
        }
        .ov-order-row { transition: background 0.15s; }
        .ov-order-row:hover { background: var(--bg-hover) !important; }
        .ov-quick-link {
          transition: background 0.18s, border-color 0.18s, color 0.18s;
        }
        .ov-quick-link:hover {
          background: var(--accent-bg) !important;
          border-color: var(--accent) !important;
          color: var(--accent) !important;
        }
        /* Responsive overrides */
        @media (max-width: 640px) {
          .ov-breakdown-grid { grid-template-columns: 1fr !important; }
          .ov-stat-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 380px) {
          .ov-stat-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        marginBottom: 28,
        paddingBottom: 20,
        borderBottom: "1px solid var(--border-subtle)",
      }}>
        <div>
          <h1 style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(28px, 5vw, 40px)",
            letterSpacing: "0.05em",
            color: "var(--text-primary)",
            lineHeight: 1,
            marginBottom: 6,
          }}>
            OVERVIEW
          </h1>
          <p style={{ fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.08em", fontFamily: "var(--font-mono)" }}>
            {today}
          </p>
        </div>

        {/* Live indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 7, height: 7, borderRadius: "50%",
            background: "var(--accent)",
            boxShadow: "0 0 8px var(--accent)",
            animation: "ovFadeIn 1s ease infinite alternate",
          }} />
          <span style={{ fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "var(--font-mono)" }}>
            Live
          </span>
        </div>
      </div>

      {/* ── Stat cards ── */}
      {stats && (
        <div
          className="ov-stat-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 14,
            marginBottom: 28,
          }}
        >
          {STAT_DEFS.map((def, i) => {
            const raw = stats[def.key];
            const display = def.key === "totalRevenue" ? fmt(raw) : raw;
            const sub = def.subFn ? def.subFn(stats) : def.sub;

            return (
              <div
                key={def.key}
                className="ov-stat-card"
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: "var(--radius-lg)",
                  padding: "18px 20px",
                  animation: `ovCardIn 0.35s ease ${i * 0.06}s both`,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Glow accent in corner */}
                <div style={{
                  position: "absolute", top: 0, right: 0,
                  width: 80, height: 80,
                  background: `radial-gradient(circle at top right, ${def.glow}, transparent 70%)`,
                  pointerEvents: "none",
                }} />

                {/* Icon */}
                <div style={{
                  color: def.color,
                  marginBottom: 12,
                  opacity: 0.8,
                }}>
                  {def.icon}
                </div>

                {/* Label */}
                <p style={{
                  fontSize: 9,
                  color: "var(--text-muted)",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontFamily: "var(--font-mono)",
                  marginBottom: 6,
                }}>
                  {def.label}
                </p>

                {/* Value */}
                <p style={{
                  fontFamily: "var(--font-display)",
                  fontSize: def.key === "totalRevenue" ? "clamp(20px, 3vw, 28px)" : "clamp(26px, 4vw, 36px)",
                  letterSpacing: "0.03em",
                  color: def.color,
                  lineHeight: 1,
                  marginBottom: 6,
                }}>
                  {display}
                </p>

                {/* Sub */}
                <p style={{ fontSize: 10, color: "var(--text-muted)", lineHeight: 1.5 }}>
                  {sub}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Breakdown + quick links ── */}
      {stats && (
        <div
          className="ov-breakdown-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 28,
          }}
        >
          {/* Payment breakdown */}
          <AdminCard style={{ animation: "ovCardIn 0.35s ease 0.25s both" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: "var(--font-mono)" }}>
                Payment Breakdown
              </span>
              <span style={{
                fontSize: 9, fontFamily: "var(--font-mono)",
                color: "var(--accent)", background: "var(--accent-bg)",
                border: "1px solid var(--accent)",
                borderRadius: "var(--radius-pill)", padding: "2px 7px",
                letterSpacing: "0.06em",
              }}>
                {stats.totalOrders} total
              </span>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {/* Visual bar */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Paid</span>
                  <span style={{ fontSize: 10, color: "var(--success-text)", fontFamily: "var(--font-mono)" }}>
                    {Math.round(paidRatio)}%
                  </span>
                </div>
                <div style={{ height: 4, background: "var(--bg-secondary)", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${paidRatio}%`,
                    background: "var(--accent)",
                    borderRadius: 999,
                    transition: "width 0.8s ease",
                  }} />
                </div>
              </div>

              {[
                { label: "Paid Orders",      value: stats.paidOrders,    color: "var(--success-text)" },
                { label: "Pending / Created", value: stats.pendingOrders, color: "var(--warning-text)" },
              ].map(r => (
                <div key={r.label} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "9px 0", borderBottom: "1px solid var(--border-subtle)",
                }}>
                  <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>{r.label}</span>
                  <span style={{
                    color: r.color, fontFamily: "var(--font-display)",
                    fontSize: 20, letterSpacing: "0.04em",
                  }}>
                    {r.value}
                  </span>
                </div>
              ))}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12 }}>
                <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>Confirmed Revenue</span>
                <span style={{ color: "var(--accent)", fontFamily: "var(--font-display)", fontSize: 24, letterSpacing: "0.04em" }}>
                  {fmt(stats.totalRevenue)}
                </span>
              </div>
            </div>
          </AdminCard>

          {/* Quick nav cards */}
          <AdminCard style={{ animation: "ovCardIn 0.35s ease 0.3s both" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border-subtle)" }}>
              <span style={{ fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: "var(--font-mono)" }}>
                Catalogue Summary
              </span>
            </div>
            <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                { label: "Products", value: stats.totalProducts, path: "/admin/products", color: "#c084fc" },
                { label: "Users",    value: stats.totalUsers,    path: "/admin/users",    color: "#34d399" },
                { label: "Orders",   value: stats.totalOrders,   path: "/admin/orders",   color: "#60a5fa" },
              ].map(item => (
                <Link
                  key={item.label}
                  to={item.path}
                  className="ov-quick-link"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 14px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-subtle)",
                    background: "var(--bg-secondary)",
                    textDecoration: "none",
                  }}
                >
                  <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{item.label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontFamily: "var(--font-display)", fontSize: 22, letterSpacing: "0.04em", color: item.color }}>
                      {item.value}
                    </span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--text-muted)" }}>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </AdminCard>
        </div>
      )}

      {/* ── Recent orders ── */}
      <AdminCard style={{ animation: "ovCardIn 0.35s ease 0.35s both" }}>
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid var(--border-subtle)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: "var(--font-mono)" }}>
            Recent Orders
          </span>
          <Link
            to="/admin/orders"
            className="ov-view-all"
            style={{
              fontSize: 11, color: "var(--accent)",
              textDecoration: "none", letterSpacing: "0.06em",
              fontFamily: "var(--font-mono)",
              transition: "color 0.15s",
            }}
          >
            View all →
          </Link>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>ID</TH>
              <TH>Customer</TH>
              <TH>Amount</TH>
              <TH>Payment</TH>
              <TH>Status</TH>
              <TH>Date</TH>
            </tr>
          </thead>
          <tbody>
            {recentOrders.map((o) => (
              <tr
                key={o.id}
                className="ov-order-row"
                style={{ background: "transparent" }}
              >
                <TD>
                  <span style={{ color: "var(--accent)", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    #{String(o.id).padStart(4, "0")}
                  </span>
                </TD>
                <TD>
                  <div style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
                    {o.userName || "—"}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2, fontFamily: "var(--font-mono)" }}>
                    {o.userEmail || ""}
                  </div>
                </TD>
                <TD>
                  <span style={{ fontFamily: "var(--font-display)", fontSize: 18, letterSpacing: "0.03em", color: "var(--accent)" }}>
                    {fmt(o.amount)}
                  </span>
                </TD>
                <TD><StatusBadge value={o.paymentStatus} /></TD>
                <TD><StatusBadge value={o.status} /></TD>
                <TD>
                  <span style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "var(--font-mono)" }}>
                    {o.orderDate ? new Date(o.orderDate).toLocaleDateString("en-IN") : "—"}
                  </span>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>

        {recentOrders.length === 0 && (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <p style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.06em" }}>
              No orders yet
            </p>
          </div>
        )}
      </AdminCard>
    </div>
  );
}
